import {
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from "@mui/material";
import MainCard from "components/MainCard";
import SimpleBar from "components/third-party/SimpleBar";
import { ISpecializationAndYearWiseMember } from "../../api/types";
import LoadingButton from "@mui/lab/LoadingButton";
import { AiFillFileExcel } from "react-icons/ai";

type Props = {
  data?: ISpecializationAndYearWiseMember[];
  exportSpecialization?: () => void;
  loading?: boolean;
};

export default function SpecializationWiseCount({
  data,
  exportSpecialization,
  loading,
}: Props) {
  if (!data || data.length === 0) {
    return (
      <MainCard darkTitle title="Specialization Wise Passout Members">
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          style={{ minHeight: "200px" }}
        >
          <Typography variant="h6" color="textSecondary">
            No data found.
          </Typography>
        </Grid>
      </MainCard>
    );
  }

  const { years, rows, yearlyTotals, grandTotal } =
    pivotSpecializationData(data);

  return (
    <MainCard
      darkTitle
      title="Specialization Wise Passout Members"
      content={false}
      secondary={
        <LoadingButton
          size="small"
          variant="contained"
          loading={loading}
          loadingPosition="start"
          color="secondary"
          startIcon={<AiFillFileExcel size={18} />}
          onClick={exportSpecialization}
        >
          Export
        </LoadingButton>
      }
    >
      <TableContainer sx={{ maxHeight: 490 }}>
        <SimpleBar sx={{ width: "100%", height: 440, overflowX: "auto" }}>
          <Table stickyHeader>
            <TableHead
              sx={{
                position: "sticky",
                top: -1,
                zIndex: 2,
              }}
            >
              <TableRow>
                <TableCell>Specialization</TableCell>
                {years.map((year) => (
                  <TableCell key={year} align="center">
                    {year}
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    position: "sticky",
                    top: -1,
                    zIndex: 5,
                  }}
                >
                  <strong>Total</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, idx) => (
                <TableRow key={idx}>
                  <TableCell>{row.specializationName}</TableCell>
                  {row.counts.map((count, i) => (
                    <TableCell key={i} align="center">
                      {count > 0 ? count : "-"}
                    </TableCell>
                  ))}
                  <TableCell align="center">
                    <strong>{row.total}</strong>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter
              sx={{
                position: "sticky",
                bottom: 0,
                backgroundColor: "white",
              }}
            >
              <TableRow>
                <TableCell>
                  <strong>Total</strong>
                </TableCell>
                {years.map((_, i) => (
                  <TableCell key={i} align="center">
                    <strong>
                      {yearlyTotals[i] > 0 ? yearlyTotals[i] : "-"}
                    </strong>
                  </TableCell>
                ))}
                <TableCell align="center">
                  <strong>{grandTotal}</strong>
                </TableCell>
              </TableRow>
            </TableFooter>
          </Table>
        </SimpleBar>
      </TableContainer>
    </MainCard>
  );
}

function pivotSpecializationData(data: ISpecializationAndYearWiseMember[]) {
  const yearSet = new Set<number>();
  const specializationMap = new Map<string, { [year: number]: number }>();

  data.forEach(({ specializationName, year, memberCount }) => {
    yearSet.add(year);
    if (!specializationMap.has(specializationName)) {
      specializationMap.set(specializationName, {});
    }
    const yearMap = specializationMap.get(specializationName)!;
    yearMap[year] = (yearMap[year] || 0) + memberCount;
  });

  const years = Array.from(yearSet).sort((a, b) => a - b);

  const rows = Array.from(specializationMap.entries()).map(
    ([specializationName, yearMap]) => {
      const counts = years.map((year) => yearMap[year] || 0);
      const total = counts.reduce((sum, val) => sum + val, 0);
      return { specializationName, counts, total };
    }
  );

  const yearlyTotals = years.map((_, idx) =>
    rows.reduce((sum, row) => sum + row.counts[idx], 0)
  );

  const grandTotal = yearlyTotals.reduce((sum, val) => sum + val, 0);

  return { years, rows, yearlyTotals, grandTotal };
}
